import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { reservasService, type DadosReserva } from "@/modules/reservas/services/reservasService";

const QUERY_KEY_RESERVAS = ["reservas"];

/** Reservas alteram a ocupação exibida no Calendário — invalidamos os dois. */
function useInvalidarReservas() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_RESERVAS });
    queryClient.invalidateQueries({ queryKey: ["calendario"] });
  };
}

export function useReservas() {
  return useQuery({ queryKey: QUERY_KEY_RESERVAS, queryFn: reservasService.listar });
}

export function useCriarReserva() {
  const invalidar = useInvalidarReservas();
  return useMutation({
    mutationFn: (dados: DadosReserva) => reservasService.criar(dados),
    onSuccess: invalidar,
  });
}

export function useAtualizarReserva() {
  const invalidar = useInvalidarReservas();
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: DadosReserva }) =>
      reservasService.atualizar(id, dados),
    onSuccess: invalidar,
  });
}

/** C-03: cancelar exige motivo (mesma UX de useAtualizarReserva/rejeitar). */
export function useCancelarReserva() {
  const invalidar = useInvalidarReservas();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string | null }) =>
      reservasService.cancelar(id, motivo),
    onSuccess: invalidar,
  });
}

/** Aprova uma reserva pendente (migration 0029) — exige podeAprovarReservas. */
export function useAprovarReserva() {
  const invalidar = useInvalidarReservas();
  return useMutation({
    mutationFn: (id: string) => reservasService.aprovar(id),
    onSuccess: invalidar,
  });
}

/** Rejeita uma reserva pendente, com motivo opcional (migration 0029). */
export function useRejeitarReserva() {
  const invalidar = useInvalidarReservas();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string | null }) =>
      reservasService.rejeitar(id, motivo),
    onSuccess: invalidar,
  });
}
